import type { ChainId } from '../../sdk'
import type { AppState } from '../../state'
import type { TransactionDetails } from '../../state/transactions/reducer'
import type { MobileSwapSession } from './mobileSwapSession'
import {
  isDismissedMobileSwapHash,
  isStaleMobileSwapPendingSession,
  readMobileSwapSession,
} from './mobileSwapSession'

/** Hash de swap exitoso persistido o inferido desde receipt en store (arranque tras MetaMask). */
export function resolveMobileSwapSuccessHash(
  transactions: AppState['transactions'],
): string | undefined {
  const session = readMobileSwapSession()
  if (!session?.hash) return undefined
  if (isDismissedMobileSwapHash(session.hash)) return undefined
  if (session.phase === 'confirmed') return session.hash
  const chainTransactions = transactions[session.chainId as ChainId]
  if (isMobileSwapSessionConfirmed(session, chainTransactions, session.hash)) return session.hash
  return undefined
}

export type SwapModalSnapshot = {
  showConfirm: boolean
  tradeToConfirm: undefined
  attemptingTxn: boolean
  swapErrorMessage: string | undefined
  txHash: string | undefined
}

const EMPTY_SWAP_MODAL: SwapModalSnapshot = {
  showConfirm: false,
  tradeToConfirm: undefined,
  attemptingTxn: false,
  swapErrorMessage: undefined,
  txHash: undefined,
}

type ChainTransactions = { [txHash: string]: TransactionDetails }

export type { ChainTransactions }

export function readSwapReceipt(
  chainTransactions: ChainTransactions | undefined,
  hash?: string,
) {
  if (!hash || !chainTransactions) return undefined
  return chainTransactions[hash]?.receipt
}

export function isMobileSwapSessionConfirmed(
  session: MobileSwapSession | null | undefined,
  chainTransactions: ChainTransactions | undefined,
  hash?: string,
): boolean {
  const resolvedHash = hash ?? session?.hash
  if (!resolvedHash) return false
  if (isDismissedMobileSwapHash(resolvedHash)) return false
  if (session?.phase === 'confirmed' && session.hash) return true
  const receipt = readSwapReceipt(chainTransactions, resolvedHash)
  return Boolean(receipt && receipt.status !== 0)
}

/** Tras volver de MetaMask / recarga: una sola pantalla de espera hasta resultado final. */
export function shouldUseMobileRecoveryPresentation(
  session: MobileSwapSession | null | undefined,
  chainTransactions: ChainTransactions | undefined,
  hash?: string,
): boolean {
  if (!session || session.dismissed || session.phase === 'failed') return false
  const resolvedHash = hash ?? session.hash
  if (resolvedHash && isDismissedMobileSwapHash(resolvedHash)) return false
  if (isStaleMobileSwapPendingSession(session)) return false
  if (isMobileSwapSessionConfirmed(session, chainTransactions, hash)) return false
  if (session.phase === 'wallet' || session.phase === 'submitted') return true
  if (session.hash) return true
  return false
}

export function isMobileSwapRecoveryActive(
  session: MobileSwapSession | null | undefined,
  chainTransactions: ChainTransactions | undefined,
  hash?: string,
): boolean {
  const resolved = session ?? readMobileSwapSession()
  if (!resolved || resolved.dismissed || resolved.phase === 'failed') return false
  return shouldUseMobileRecoveryPresentation(resolved, chainTransactions, hash ?? resolved.hash)
}

export function deriveSwapModalStateFromSession(
  session: MobileSwapSession | null,
  transactions: AppState['transactions'],
): SwapModalSnapshot {
  if (!session) return EMPTY_SWAP_MODAL

  const chainTransactions = transactions[session.chainId as ChainId]
  const hash = session.hash
  if (hash) {
    const receipt = readSwapReceipt(chainTransactions, hash)
    const isConfirmed = isMobileSwapSessionConfirmed(session, chainTransactions, hash)
    return {
      showConfirm: !isConfirmed,
      tradeToConfirm: undefined,
      attemptingTxn: !isConfirmed,
      swapErrorMessage: receipt?.status === 0 ? 'Transaction reverted on-chain.' : undefined,
      txHash: hash,
    }
  }

  if (session.phase === 'wallet' || session.phase === 'submitted') {
    return {
      showConfirm: true,
      tradeToConfirm: undefined,
      attemptingTxn: true,
      swapErrorMessage: undefined,
      txHash: undefined,
    }
  }

  return EMPTY_SWAP_MODAL
}
